import { useEffect, useState } from "react";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
    ["Services", "#services"],
    ["Why Us", "#why-us"],
    ["Projects", "#projects"],
    ["Reviews", "#reviews"],
    ["Quote", "#contact"],
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { scrollYProgress } = useScroll();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <motion.div
                className="fixed top-0 left-0 h-[2px] bg-amber-500 origin-left z-[80]"
                style={{ scaleX: scrollYProgress, width: "100%" }}
            />
            <header
                data-testid="main-navbar"
                className={`fixed top-0 inset-x-0 z-[70] px-4 sm:px-8 py-3 transition-colors duration-300 border-b ${
                    scrolled
                        ? "bg-[#0B0F12]/90 backdrop-blur-xl border-white/10"
                        : "bg-[#0B0F12]/40 backdrop-blur-md border-white/5"
                }`}
            >
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                    <a href="#top" data-testid="brand-logo-link" className="shrink-0">
                        <Logo />
                    </a>

                    <span className="hidden lg:flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#10B981] border border-white/10 px-3 py-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                        Open · Closes 5 pm
                    </span>

                    <nav className="hidden md:flex items-center gap-7">
                        {LINKS.map(([label, href]) => (
                            <a
                                key={href}
                                href={href}
                                data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-sm text-[#94A3B8] hover:text-white transition-colors"
                            >
                                {label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <a
                            href="tel:+233553237357"
                            data-testid="nav-call-btn-0553237357"
                            className="hidden sm:flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0F172A] text-sm font-semibold px-4 py-2.5 transition-colors"
                        >
                            <Phone size={15} strokeWidth={2.5} />
                            055 323 7357
                        </a>
                        <button
                            data-testid="nav-mobile-menu-button"
                            onClick={() => setOpen((v) => !v)}
                            aria-label="Menu"
                            className="md:hidden text-white p-2 border border-white/10"
                        >
                            {open ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-overlay"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[65] bg-[#0B0F12]/97 backdrop-blur-xl md:hidden flex flex-col justify-center px-8 gap-2"
                    >
                        {LINKS.map(([label, href], i) => (
                            <a
                                key={href}
                                href={href}
                                onClick={() => setOpen(false)}
                                className="font-display font-bold uppercase text-4xl text-[#F8FAFC] py-3 border-b border-white/10"
                            >
                                <span className="font-mono2 text-xs text-amber-500 mr-4">0{i + 1}</span>
                                {label}
                            </a>
                        ))}
                        <div className="mt-8 flex flex-col gap-3">
                            <a href="tel:+233553237357" className="bg-amber-500 text-[#0F172A] font-semibold px-5 py-4 text-center">
                                Call 055 323 7357
                            </a>
                            <a href="tel:+233279912807" className="border border-white/20 text-white px-5 py-4 text-center">
                                Call 027 991 2807
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Nav;
